import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Car, Menu, X, UploadCloud, ExternalLink, MapPin, Compass, Phone, MessageCircle, Mail, Calendar, Luggage, Globe } from 'lucide-react';
import { EASY_WAY_CONTACT } from '../data/contactInfo';
import { useLanguage } from '../context/LanguageContext';
import { getLocalizedRoute, translateCurrentPath } from '../utils/navigation';
import { Language } from '../types';

interface NavbarProps {
  onSelectCategory: (cat: any) => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onSelectCategory }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, t } = useLanguage();

  const handleNav = (path: string) => {
    setMobileMenuOpen(false);
    const targetUrl = path === '/' 
      ? (lang === 'es' ? '/' : `/${lang}/`)
      : getLocalizedRoute(path, lang);
    navigate(targetUrl);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const handleLanguageChange = (newLang: Language) => {
    const targetUrl = translateCurrentPath(location.pathname, newLang);
    navigate(targetUrl);
  };

  const isHomeActive = location.pathname === '/' || location.pathname === `/${lang}/` || location.pathname === `/${lang}`;

  return (
    <header className="sticky top-0 z-40 bg-[#073B4C] text-white border-b border-[#052a37] shadow-md transition-all">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link 
          to={lang === 'es' ? '/' : `/${lang}/`} 
          className="flex items-center gap-3 group"
          id="main-brand-logo"
        >
          <div className="w-10 h-10 rounded-xl bg-transparent flex items-center justify-center overflow-hidden">
            <img 
              src="/media/dominios/cancunmexico.travel/icono.webp" 
              alt="Cancun Mexico Travel Logo" 
              width={40} 
              height={40} 
              loading="eager" 
              decoding="async" 
              className="w-10 h-10 object-contain" 
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-baseline gap-1">
              <span className="font-extrabold text-xl tracking-tight text-white font-['Outfit',sans-serif]">
                CancunMexico<span className="text-[#D6A84B]">.travel</span>
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-[#F7F3EA]/70 font-semibold">
              {lang === 'pt' ? 'Guia Turístico & Autonomia Total na Estrada' : lang === 'en' ? 'Travel Guide & Total Road Autonomy' : lang === 'pt' ? 'Guía Turística & Movilidad Sin Límites' : 'Guía Turística & Movilidad Sin Límites'}
            </span>
          </div>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden lg:flex items-center gap-5 text-sm font-semibold text-[#F7F3EA]/90">
          <button 
            onClick={() => handleNav('/')}
            className={`hover:text-[#D6A84B] transition-colors cursor-pointer ${isHomeActive ? 'text-[#D6A84B] font-bold border-b-2 border-[#D6A84B] pb-0.5' : ''}`}
          >
            {t.nav.destinations}
          </button>
          <button 
            onClick={() => handleNav('eventos')}
            className={`hover:text-[#D6A84B] transition-colors cursor-pointer ${location.pathname === getLocalizedRoute('eventos', lang) ? 'text-[#D6A84B] font-bold border-b-2 border-[#D6A84B] pb-0.5' : ''}`}
          >
            {t.nav.events}
          </button>
          <button 
            onClick={() => handleNav('rutas')}
            className={`hover:text-[#D6A84B] transition-colors cursor-pointer ${location.pathname === getLocalizedRoute('rutas', lang) ? 'text-[#D6A84B] font-bold border-b-2 border-[#D6A84B] pb-0.5' : ''}`}
          >
            {t.nav.roadtrips}
          </button>
          <button 
            onClick={() => handleNav('consejos-manejo')}
            className={`hover:text-[#D6A84B] transition-colors cursor-pointer ${location.pathname === getLocalizedRoute('consejos-manejo', lang) ? 'text-[#D6A84B] font-bold border-b-2 border-[#D6A84B] pb-0.5' : ''}`}
          >
            {t.nav.drivingGuide}
          </button>
          <button 
            onClick={() => handleNav('consejos-viaje')}
            className={`hover:text-[#D6A84B] transition-colors cursor-pointer ${location.pathname === getLocalizedRoute('consejos-viaje', lang) ? 'text-[#D6A84B] font-bold border-b-2 border-[#D6A84B] pb-0.5' : ''}`}
          >
            {t.nav.travelTips}
          </button>
        </div>

        {/* Right action buttons & Language Toggle */}
        <div className="flex items-center gap-3">
          {/* Main Language Toggle Pill */}
          <div className="flex items-center bg-white/10 border border-white/20 rounded-lg p-1 text-xs font-bold" id="nav-language-selector">
            <Globe className="w-3.5 h-3.5 text-[#D6A84B] mr-1.5 hidden sm:inline" />
            <button
              onClick={() => handleLanguageChange('es')}
              id="lang-btn-es"
              className={`px-2 py-1 rounded transition-all cursor-pointer ${lang === 'es' ? 'bg-[#D6A84B] text-[#073B4C] font-extrabold shadow-sm' : 'text-white/80 hover:text-white'}`}
              aria-label="ES - Seleccionar Español"
            >
              ES
            </button>
            <button
              onClick={() => handleLanguageChange('en')}
              id="lang-btn-en"
              className={`px-2 py-1 rounded transition-all cursor-pointer ${lang === 'en' ? 'bg-[#D6A84B] text-[#073B4C] font-extrabold shadow-sm' : 'text-white/80 hover:text-white'}`}
              aria-label="EN - Select English"
            >
              EN
            </button>
            <button
              onClick={() => handleLanguageChange('pt')}
              id="lang-btn-pt"
              className={`px-2 py-1 rounded transition-all cursor-pointer ${lang === 'pt' ? 'bg-[#D6A84B] text-[#073B4C] font-extrabold shadow-sm' : 'text-white/80 hover:text-white'}`}
              aria-label="PT - Selecionar Português"
            >
              PT
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile horizontal scrolling nav */}
      <div className="lg:hidden border-t border-white/10 bg-[#052a37] px-4 pb-3 pt-2">
        <div className="flex items-center gap-2.5 overflow-x-auto snap-x hide-scrollbar">
          <button 
            onClick={() => handleNav('/')}
            className={`snap-start shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold border flex items-center gap-2 transition-all duration-300 ${isHomeActive ? 'bg-[#0F766E] border-[#0F766E] text-white shadow-sm' : 'bg-[#073B4C] border-white/15 text-[#F7F3EA] hover:bg-[#0d4e64]'}`}
          >
            <MapPin className={`w-3.5 h-3.5 ${isHomeActive ? 'text-white' : 'text-[#D6A84B]'}`} />
            {t.nav.destinations}
          </button>
          <button 
            onClick={() => handleNav('eventos')}
            className={`snap-start shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold border flex items-center gap-2 transition-all duration-300 ${location.pathname === getLocalizedRoute('eventos', lang) ? 'bg-[#0F766E] border-[#0F766E] text-white shadow-sm' : 'bg-[#073B4C] border-white/15 text-[#F7F3EA] hover:bg-[#0d4e64]'}`}
          >
            <Calendar className={`w-3.5 h-3.5 ${location.pathname === getLocalizedRoute('eventos', lang) ? 'text-white' : 'text-[#D6A84B]'}`} />
            {t.nav.events}
          </button>
          <button 
            onClick={() => handleNav('rutas')}
            className={`snap-start shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold border flex items-center gap-2 transition-all duration-300 ${location.pathname === getLocalizedRoute('rutas', lang) ? 'bg-[#0F766E] border-[#0F766E] text-white shadow-sm' : 'bg-[#073B4C] border-white/15 text-[#F7F3EA] hover:bg-[#0d4e64]'}`}
          >
            <Compass className={`w-3.5 h-3.5 ${location.pathname === getLocalizedRoute('rutas', lang) ? 'text-white' : 'text-[#D6A84B]'}`} />
            {t.nav.roadtrips}
          </button>
          <button 
            onClick={() => handleNav('consejos-manejo')}
            className={`snap-start shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold border flex items-center gap-2 transition-all duration-300 ${location.pathname === getLocalizedRoute('consejos-manejo', lang) ? 'bg-[#0F766E] border-[#0F766E] text-white shadow-sm' : 'bg-[#073B4C] border-white/15 text-[#F7F3EA] hover:bg-[#0d4e64]'}`}
          >
            <Car className={`w-3.5 h-3.5 ${location.pathname === getLocalizedRoute('consejos-manejo', lang) ? 'text-white' : 'text-[#D6A84B]'}`} />
            {t.nav.drivingGuide}
          </button>
          <button 
            onClick={() => handleNav('consejos-viaje')}
            className={`snap-start shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold border flex items-center gap-2 transition-all duration-300 ${location.pathname === getLocalizedRoute('consejos-viaje', lang) ? 'bg-[#0F766E] border-[#0F766E] text-white shadow-sm' : 'bg-[#073B4C] border-white/15 text-[#F7F3EA] hover:bg-[#0d4e64]'}`}
          >
            <Luggage className={`w-3.5 h-3.5 ${location.pathname === getLocalizedRoute('consejos-viaje', lang) ? 'text-white' : 'text-[#D6A84B]'}`} />
            {t.nav.travelTips}
          </button>
        </div>
      </div>
    </header>
  );
};
